import React, { useState, useRef, useEffect } from "react";
import "./chat-input.component.scoped.scss";
import { TextField, Box, Divider, Button, IconButton } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import ChatMic from "../chat-mic/chat-mic.component";
import ChatAttach from "../chat-attach/chat-attach.component";
import ChatSend from "../chat-send/chat-send.component";
import { useTyping } from "../../context/typing-context";
import DeleteIcon from "@material-ui/icons/Close";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";

interface ChatInputProps {
  onSendMessage: (message: string, attachment?: File | File[]) => void;
  onExitChat?: () => void; // New prop for exit handler
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
  requiresFile?: boolean; // Flag to indicate if file upload is required for current question
  autoFocus?: boolean; // New prop to trigger auto focus
  language?: "en-US" | "de-DE"; // Language for ChatMic
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onExitChat,
  disabled = false,
  placeholder,
  maxRows = 12,
  requiresFile = false,
  autoFocus = false,
  language = "en-US",
}) => {
  const { t } = useTranslation();
  const { isTypingComplete } = useTyping();
  const [message, setMessage] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatMicRef = useRef<any>(null);

  const defaultPlaceholder = placeholder || t("CHATBOT.ENTER_PROMPT");

  const handleInput = () => {
    // Handle any input event including paste, drop, etc.
    setTimeout(() => {
      if (textareaRef.current) {
        setMessage(textareaRef.current.value);
      }
    }, 0);
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMessage(value);
  };

  const handleFileDelete = (fileIndex: number) => {
    setAttachedFiles((prev) => prev.filter((_, idx) => idx !== fileIndex));
  };

  const handleFileSelect = (files: File[] | null) => {
    if (files && files.length > 0) {
      // Check file sizes (limit to 2MB each)
      const maxSize = 2 * 1024 * 1024; // 2MB
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach((file) => {
        if (file.size > maxSize) {
          invalidFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        console.error(
          `Files too large. Maximum size is 2MB: ${invalidFiles.join(", ")}`,
        );
        // You could add a toast notification here
      }

      if (validFiles.length > 0) {
        // Add new files to existing attachedFiles, prevent duplicates by name and size
        setAttachedFiles((prev) => {
          const allFiles = [...prev, ...validFiles];
          // Remove duplicates by name and size
          const uniqueFiles = allFiles.filter(
            (file, idx, arr) =>
              arr.findIndex(
                (f) => f.name === file.name && f.size === file.size,
              ) === idx,
          );
          return uniqueFiles;
        });
      }
    } else {
      setAttachedFiles([]);
    }
  };

  const handleSendMessage = () => {
    if (message.trim() && !disabled) {
      // Stop recording if active
      if (
        chatMicRef.current &&
        typeof chatMicRef.current.stopRecording === "function"
      ) {
        chatMicRef.current.stopRecording();
      }
      // Send both message and files
      onSendMessage(
        message.trim(),
        attachedFiles.length > 0 ? attachedFiles : undefined,
      );
      setMessage("");
      setAttachedFiles([]);
    }
  };

  const handleTranscript = (transcript: string) => {
    setMessage((prev) => prev + transcript);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const isSendButtonEnabled =
    message.trim().length > 0 && !disabled && isTypingComplete;

  // Auto focus the input field when autoFocus prop changes
  useEffect(() => {
    if (autoFocus && !disabled && isTypingComplete) {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [autoFocus, disabled, isTypingComplete]);

  return (
    <Box className="chat-input-container">
      {/* Show attached files above the text field */}
      {attachedFiles.length > 0 && (
        <Box className="attached-files-list">
          {attachedFiles.map((file, idx) => (
            <Box key={idx} className="attached-file-row">
              <PictureAsPdfOutlinedIcon className="pdf-icon" />
              <span className="attached-file-name">
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </span>
              <IconButton size="small" onClick={() => handleFileDelete(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <TextField
        id="chat-input"
        inputRef={textareaRef}
        className="chat-input-field"
        variant="outlined"
        multiline
        rows={1}
        rowsMax={maxRows}
        value={message}
        onChange={handleMessageChange}
        onKeyPress={handleKeyPress}
        onPaste={handleInput}
        onInput={handleInput}
        placeholder={defaultPlaceholder}
        disabled={disabled || !isTypingComplete}
      />
      <Box className="chat-input-actions">
        <ChatAttach
          onFileSelect={handleFileSelect}
          disabled={disabled || !isTypingComplete || !requiresFile}
          isMultiple={true}
          accept=".pdf"
        />
        <Divider orientation="vertical" flexItem className="icon-divider" />
        <ChatMic
          ref={chatMicRef}
          onTranscript={handleTranscript}
          disabled={disabled || !isTypingComplete}
          language={language as "en-US" | "de-DE"}
        />
        <Divider orientation="vertical" flexItem className="icon-divider" />
        {onExitChat && (
          <Button
            className="leave-chat-button"
            onClick={onExitChat}
            size="small"
          >
            {t("CHATBOT.LEAVE_CHAT")}
          </Button>
        )}
        <ChatSend
          onSend={handleSendMessage}
          disabled={disabled || !isTypingComplete}
          enabled={isSendButtonEnabled}
        />
      </Box>
      <Box className="chat-input-disclaimer">{t("CHATBOT.DISCLAIMER")}</Box>
    </Box>
  );
};

export default ChatInput;
